import { Entity, EntityType, getEntityNumericId } from "@/queries/folder-entities/types";
import { WorkspaceMember } from "@/types/workspaceMembers";
import { routePaths } from "@/utils/routePaths";

export interface TagUser {
  type: "user";
  id: number;
  name: string;
  email: string;
}

export interface TagEntity {
  type: "entity";
  entity: Entity;
}

export interface TagEntityType {
  type: "entityType";
  entityType: EntityType;
  displayName: string;
}

export type TagSuggestionItem = TagUser | TagEntity | TagEntityType;

export interface TagMentionAttrs {
  tagType: "user" | "entity" | "entityType";
  id?: number | string;
  name: string;
  email?: string;
  entityType?: EntityType;
  link?: string;
}

export const isTagUser = (item: TagSuggestionItem): item is TagUser => item.type === "user";

export const isTagEntity = (item: TagSuggestionItem): item is TagEntity => item.type === "entity";

export const isTagEntityType = (item: TagSuggestionItem): item is TagEntityType =>
  item.type === "entityType";

const getEmailPrefix = (email: string): string => {
  const atIndex = email.indexOf("@");
  return atIndex > 0 ? email.substring(0, atIndex) : email;
};

export const workspaceMemberToTagUser = (member: WorkspaceMember): TagUser | null => {
  if (!member.user_id || !member.email) {
    return null;
  }
  return {
    type: "user",
    id: member.user_id,
    name: member.name || getEmailPrefix(member.email),
    email: member.email,
  };
};

export const entityToTagEntity = (entity: Entity): TagEntity => ({
  type: "entity",
  entity,
});

const VERSIONED_ENTITY_PATHS: Partial<Record<EntityType, string>> = {
  [EntityType.PROMPT]: "prompt",
  [EntityType.WORKFLOW]: "workflow",
};

const getLatestVersionNumber = (entity: Entity): number | undefined => {
  const { metadata } = entity;
  const metadataVersion =
    metadata && "latest_version_number" in metadata ? metadata.latest_version_number : undefined;
  const latestVersion = entity.version_number ?? metadataVersion;
  return latestVersion && latestVersion > 0 ? latestVersion : undefined;
};

export const getEntityTagDisplayName = (entity: Entity): string => {
  const latestVersion = getLatestVersionNumber(entity);
  if (!latestVersion || !VERSIONED_ENTITY_PATHS[entity.type]) {
    return entity.name;
  }
  return `${entity.name} v${latestVersion}`;
};

export const entityToTagMentionAttrs = (
  entity: Entity,
  workspaceId?: number | string,
): TagMentionAttrs => {
  const latestVersion = getLatestVersionNumber(entity);
  const versionedPath = VERSIONED_ENTITY_PATHS[entity.type];
  let link: string | undefined;
  if (workspaceId && latestVersion && versionedPath) {
    link = `/workspace/${workspaceId}/${versionedPath}/${entity.id}/version/${latestVersion}`;
  } else if (workspaceId && entity.type === EntityType.SMART_TABLE) {
    link = routePaths.workspaceSmartTable(workspaceId, entity.id);
  } else if (workspaceId && entity.type === EntityType.TOOL) {
    link = routePaths.workspaceTool(workspaceId, entity.id);
  } else if (workspaceId && entity.type === EntityType.AB_TEST) {
    link = routePaths.workspaceAbTest(workspaceId, entity.id);
  }

  return {
    tagType: "entity",
    id: getEntityNumericId(entity.id) ?? entity.id,
    name: getEntityTagDisplayName(entity),
    entityType: entity.type,
    link,
  };
};
